<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useLoadRoleObjectTypes } from '@/features/Role/RoleEdit'
import type { TypePermission } from '@/entities/Role'
import './assets/style.scss'
import type { PermissionFlag } from '@/entities/Auth'

const props = defineProps<{
  modelValue: TypePermission[]
  groupId: number
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: TypePermission[]): void
}>()

const show = defineModel<boolean>('show', { default: false })

const objectTypesQuery = useLoadRoleObjectTypes(computed(() => props.groupId))
const objectTypes = computed(() => {
  const payload = objectTypesQuery.data.value?.payload
  const raw = Array.isArray(payload?.object_types) ? payload.object_types : []
  return raw
    .map((item: any) => ({
      id: Number(item.id),
      name: String(item.name || item.object_name || `#${item.id}`),
      is_system: Boolean(item.is_system),
    }))
    .filter((item: any) => Number.isFinite(item.id) && !item.is_system)
    .sort((a: any, b: any) => a.name.localeCompare(b.name))
})

// localPerms only holds types that are explicitly in custom (restricted) mode.
// Types NOT in localPerms are in default (unrestricted) mode.
const localPerms = ref<Record<number, TypePermission>>({})

const isCustom = (typeId: number): boolean => !!localPerms.value[typeId]

const ensurePerm = (objectTypeId: number): TypePermission => {
  if (!localPerms.value[objectTypeId]) {
    localPerms.value[objectTypeId] = {
      object_type_id: objectTypeId,
      read: true,
      create: true,
      update: true,
      delete: true,
      relocate: true,
    }
  }
  return localPerms.value[objectTypeId]
}

const restrictType = (typeId: number) => {
  ensurePerm(typeId)
}

const resetType = (typeId: number) => {
  const copy = { ...localPerms.value }
  delete copy[typeId]
  localPerms.value = copy
}

const initLocalPerms = () => {
  const map: Record<number, TypePermission> = {}
  for (const p of props.modelValue ?? []) {
    map[p.object_type_id] = { ...p }
  }
  localPerms.value = map
}

watch(show, (visible) => {
  if (visible) initLocalPerms()
})

watch(
  () => props.modelValue,
  () => {
    if (show.value) initLocalPerms()
  },
)

watch(
  () => objectTypes.value,
  () => {
    if (show.value) initLocalPerms()
  },
)

const onReadChange = (typeId: number, value: boolean) => {
  const p = ensurePerm(typeId)
  p.read = value
  if (!value) {
    p.create = false
    p.update = false
    p.delete = false
    p.relocate = false
  }
}

/**
 * Описание чекбоксов прав для отрисовки циклом.
 * `master` — флаг «Чтение», который управляет доступностью остальных.
 * `default` — значение по умолчанию, когда флаг ещё не задан.
 */
const permissionFields: ReadonlyArray<{
  key: PermissionFlag
  label: string
  master?: boolean
  default?: boolean
}> = [
  { key: 'read', label: 'Чтение', master: true, default: false },
  { key: 'create', label: 'Создание', default: false },
  { key: 'update', label: 'Изменение', default: false },
  { key: 'delete', label: 'Удаление', default: false },
  { key: 'relocate', label: 'Перемещение', default: true },
]

/**
 * Универсальный обработчик изменения чекбокса права.
 * Флаг «Чтение» каскадно сбрасывает остальные права, прочие флаги
 * выставляются напрямую.
 */
const onPermChange = (typeId: number, key: PermissionFlag, value: boolean) => {
  if (key === 'read') {
    onReadChange(typeId, value)
    return
  }
  ensurePerm(typeId)[key] = value
}

const save = () => {
  // Emit ALL types that are in custom mode, even if all flags are false
  // (explicit deny-all is a valid state). Types not in localPerms use
  // default unrestricted behaviour and are not stored.
  const result = Object.values(localPerms.value)
  emit('update:modelValue', result)
  show.value = false
}
</script>

<template>
  <VDialog v-model="show" max-width="720">
    <VCard>
      <VCardTitle class="element-permissions__title">
        <span>Права доступа к элементам</span>
      </VCardTitle>

      <VDivider />

      <VCardText class="element-permissions__body">
        <VList class="element-permissions__list" lines="two">
          <template v-for="(t, index) in objectTypes" :key="t.id">
            <VDivider v-if="index > 0" class="element-permissions__divider" />

            <VListItem class="element-permissions__item">
              <template #title>
                <span class="element-permissions__name">{{ t.name }}</span>
              </template>

              <template #subtitle>
                <p
                  v-if="!isCustom(t.id)"
                  class="element-permissions__status element-permissions__status_full"
                >
                  Полный доступ
                </p>
                <p
                  v-else
                  class="element-permissions__status element-permissions__status_restricted"
                >
                  Ограниченный доступ
                </p>
              </template>

              <template #append>
                <VBtn
                  v-if="!isCustom(t.id)"
                  size="small"
                  variant="tonal"
                  color="primary"
                  prepend-icon="edit"
                  @click="restrictType(t.id)"
                >
                  Ограничить
                </VBtn>
                <VBtn
                  v-else
                  size="small"
                  variant="text"
                  color="error"
                  prepend-icon="restart_alt"
                  @click="resetType(t.id)"
                >
                  Сбросить
                </VBtn>
              </template>
            </VListItem>

            <div v-if="isCustom(t.id)" class="element-permissions__perms-wrap">
              <div class="element-permissions__perms">
                <VCheckbox
                  v-for="field in permissionFields"
                  :key="field.key"
                  :model-value="localPerms[t.id]?.[field.key] ?? field.default"
                  @update:model-value="(v) => onPermChange(t.id, field.key, Boolean(v))"
                  :label="field.label"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="!field.master && !localPerms[t.id]?.read"
                />
              </div>
            </div>
          </template>
        </VList>

        <div v-if="!objectTypes.length" class="element-permissions__empty">
          <VIcon icon="folder_open" size="48" />
          Нет доступных элементов
        </div>
      </VCardText>

      <VDivider />

      <VCardActions class="element-permissions__actions">
        <VBtn color="default" variant="tonal" @click="show = false">Отмена</VBtn>
        <VBtn color="primary" variant="flat" @click="save">Сохранить</VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
